import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, Play, Pause, Trash2, Plus, RefreshCw } from 'lucide-react';
import { beesClient } from '../../api/beesClient.js';

interface ScheduledJob {
  id: string;
  name: string;
  cronExpression: string;
  nextRun: string;
  lastRun?: string;
  status: 'active' | 'paused' | 'error';
  description?: string;
}

interface SchedulerData {
  jobs: ScheduledJob[];
  totalJobs: number;
  activeJobs: number;
  nextExecution?: string;
  systemStatus: 'running' | 'paused' | 'error';
}

const SchedulerPanel: React.FC = () => {
  const [data, setData] = useState<SchedulerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchSchedulerData = async () => {
    try {
      setError(null);
      const response = await beesClient.runScheduler({ action: 'list' });
      
      if (response.success && response.data) {
        const transformedData: SchedulerData = {
          jobs: [],
          totalJobs: 0,
          activeJobs: 0,
          systemStatus: 'running'
        };
        setData(transformedData);
      } else {
        const mockData: SchedulerData = {
          jobs: [
            {
              id: 'health-check-job',
              name: 'Health Check',
              cronExpression: '*/5 * * * *',
              nextRun: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
              lastRun: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
              status: 'active',
              description: 'Runs health checks every 5 minutes'
            },
            {
              id: 'metrics-collection',
              name: 'Metrics Collection',
              cronExpression: '0 */1 * * *',
              nextRun: new Date(Date.now() + 45 * 60 * 1000).toISOString(),
              lastRun: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
              status: 'active',
              description: 'Collects system metrics hourly'
            },
            {
              id: 'repo-stats-update',
              name: 'Repository Stats Update',
              cronExpression: '0 0 * * *',
              nextRun: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString(),
              lastRun: new Date(Date.now() - 16 * 60 * 60 * 1000).toISOString(),
              status: 'paused',
              description: 'Updates repository statistics daily'
            }
          ],
          totalJobs: 3,
          activeJobs: 2,
          nextExecution: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
          systemStatus: 'running'
        };
        setData(mockData);
      }
    } catch (err) {
      console.error('Failed to fetch scheduler data:', err);
      setError('Failed to load scheduler data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchSchedulerData();
    const interval = setInterval(fetchSchedulerData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchSchedulerData();
  };

  const handleJobAction = async (jobId: string, action: 'pause' | 'resume' | 'delete') => {
    try {
      const response = await beesClient.runScheduler({ 
        action, 
        jobId 
      });
      
      if (response.success) {
        await fetchSchedulerData();
      } else {
        setError(`Failed to ${action} job: ${response.error || 'Unknown error'}`);
      }
    } catch (err) {
      setError(`Failed to ${action} job: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const formatCronExpression = (cron: string): string => {
    const parts = cron.split(' ');
    if (parts.length !== 5) return cron;
    
    const [minute, hour, _day, _month, dayOfWeek] = parts;
    
    if (minute === '*/5' && hour === '*') return 'Every 5 minutes';
    if (minute === '0' && hour === '*/1') return 'Every hour';
    if (minute === '0' && hour === '0') return 'Daily at midnight';
    if (minute === '0' && hour === '0' && dayOfWeek === '0') return 'Weekly on Sunday';
    
    return cron;
  };

  const formatDateTime = (isoString: string): string => {
    return new Date(isoString).toLocaleString();
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'active': return 'text-green-400';
      case 'paused': return 'text-yellow-400';
      case 'error': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <Play size={16} className="text-green-400" />;
      case 'paused': return <Pause size={16} className="text-yellow-400" />;
      case 'error': return <Trash2 size={16} className="text-red-400" />;
      default: return <Clock size={16} className="text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-lg font-semibold cream-gradient-text text-drop-shadow mb-6 flex items-center">
            <div className="w-2 h-2 bg-brand-accent rounded-full mr-3"></div>
            Scheduler Status
          </h2>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="glass-dark border border-white/10 rounded-xl p-4 animate-pulse">
                <div className="h-4 bg-slate-700 rounded mb-2"></div>
                <div className="h-3 bg-slate-700 rounded mb-3 w-1/2"></div>
                <div className="h-2 bg-slate-700 rounded"></div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-lg font-semibold cream-gradient-text text-drop-shadow mb-6 flex items-center">
            <div className="w-2 h-2 bg-brand-accent rounded-full mr-3"></div>
            Scheduler Status
          </h2>
          <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4">
            <div className="text-red-400 text-sm">⚠️ {error}</div>
            <button 
              onClick={handleRefresh}
              className="text-xs text-red-300 hover:text-red-200 mt-2 underline"
            >
              Retry
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-lg font-semibold cream-gradient-text text-drop-shadow mb-6 flex items-center">
            <div className="w-2 h-2 bg-brand-accent rounded-full mr-3"></div>
            Scheduler Status
          </h2>
          <div className="glass-dark border border-white/10 rounded-xl p-4">
            <div className="text-brand-text text-sm">No scheduler data available</div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold cream-gradient-text text-drop-shadow flex items-center">
            <div className="w-2 h-2 bg-brand-accent rounded-full mr-3"></div>
            Scheduler Status
          </h2>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 bg-indigo-500/20 hover:bg-indigo-500/30 border border-indigo-500/30 rounded-lg text-indigo-400 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="glass-dark border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-brand-text text-sm font-medium">Total Jobs</div>
                <div className="text-2xl font-bold text-brand-text">{data.totalJobs}</div>
              </div>
              <Calendar className="text-indigo-400" size={24} />
            </div>
          </div>

          <div className="glass-dark border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-brand-text text-sm font-medium">Active Jobs</div>
                <div className="text-2xl font-bold text-green-400">{data.activeJobs}</div>
              </div>
              <Play className="text-green-400" size={24} />
            </div>
          </div>

          <div className="glass-dark border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-brand-text text-sm font-medium">System Status</div>
                <div className={`text-sm font-medium ${getStatusColor(data.systemStatus)}`}>
                  {data.systemStatus.charAt(0).toUpperCase() + data.systemStatus.slice(1)}
                </div>
              </div>
              {getStatusIcon(data.systemStatus)}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-md font-medium cream-gradient-text text-drop-shadow">Scheduled Jobs</h3>
            <button className="violett-button text-drop-shadow p-2 rounded-lg transition-colors">
              <Plus size={16} />
            </button>
          </div>

          {data.jobs.map((job) => (
            <motion.div
              key={job.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="glass-dark border border-white/10 rounded-xl p-4 hover:border-slate-600/50 transition-all duration-300"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {getStatusIcon(job.status)}
                    <h4 className="cream-gradient-text text-drop-shadow font-medium">{job.name}</h4>
                    <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(job.status)} bg-current/10`}>
                      {job.status}
                    </span>
                  </div>
                  
                  {job.description && (
                    <p className="text-slate-400 text-sm mb-3">{job.description}</p>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                    <div>
                      <span className="text-slate-500">Schedule:</span>
                      <div className="text-brand-text">{formatCronExpression(job.cronExpression)}</div>
                    </div>
                    <div>
                      <span className="text-slate-500">Next Run:</span>
                      <div className="text-brand-text">{formatDateTime(job.nextRun)}</div>
                    </div>
                    {job.lastRun && (
                      <div>
                        <span className="text-slate-500">Last Run:</span>
                        <div className="text-brand-text">{formatDateTime(job.lastRun)}</div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-2 ml-4">
                  {job.status === 'active' ? (
                    <button
                      onClick={() => handleJobAction(job.id, 'pause')}
                      className="p-2 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/30 rounded-lg text-yellow-400 transition-colors"
                      title="Pause job"
                    >
                      <Pause size={14} />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleJobAction(job.id, 'resume')}
                      className="p-2 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 rounded-lg text-green-400 transition-colors"
                      title="Resume job"
                    >
                      <Play size={14} />
                    </button>
                  )}
                  <button
                    onClick={() => handleJobAction(job.id, 'delete')}
                    className="p-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-lg text-red-400 transition-colors"
                    title="Delete job"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {data.nextExecution && (
          <div className="glass-dark border border-indigo-500/30 rounded-xl p-4 mt-4">
            <div className="flex items-center gap-3">
              <Clock className="text-indigo-400" size={20} />
              <div>
                <div className="text-brand-text font-medium">Next Execution</div>
                <div className="text-indigo-400 text-sm">{formatDateTime(data.nextExecution)}</div>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default SchedulerPanel;
